import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";

// Interfaces
import { ILocationState } from "@/Common/interfaces/location";
import { IPayPeriodTreeProps, IPeriodTreeLeaf } from "./interfaces";

// API
import { getCommissionCycles } from "@/Common/api/commissionCycles";

// Hooks
import { useLocationId } from "@Common/hooks/Location/useLocationId.tsx";

// Helpers
import { getPayPeriodTree } from "./utils";

// MUI
import { Grid } from "@mui/material";

// Components
import AppSideBar from "@/Layouts/components/SideBars/AppSideBar";

// Constants
const ACTIVE_STATUSES = ["ACTIVE", "CLOSING", "POSTED"];
const isActiveCommissionCycle = (periodLeaf: { cycleStatus?: string } | null) =>
	ACTIVE_STATUSES.includes(String(periodLeaf?.cycleStatus).toUpperCase());

const getPartnerPeriodId = (selection: Partial<IPeriodTreeLeaf> | null, isMultiple = false) => {
	if (!selection) {
		return "";
	}
	const periodId = selection?.payPeriodId ?? "";
	const partnerCycleId = selection?.nextCCId ?? selection?.previousCCId ?? "";

	if (!periodId || !partnerCycleId || !isMultiple) {
		return "";
	}
	return `${partnerCycleId}_${periodId}`;
};

const getOrderedSelectedItems = (selection: Partial<IPeriodTreeLeaf> | null, selectBy = "_id", isMultiple = false) => {
	if (!selection) {
		return "";
	}

	const defaultSelection = selection?.[selectBy as keyof object] ?? "";
	const additionalSelection = getPartnerPeriodId(selection, isMultiple);
	if (!isMultiple || !additionalSelection) {
		return [defaultSelection];
	}

	const itemsSelected = selection?.nextCCId ? [defaultSelection, additionalSelection] : [additionalSelection, defaultSelection];
	const filtered = itemsSelected.filter((item) => item);
	return [...new Set(filtered)];
};

const getExpandedSelection = (args: {
	selection: Partial<IPeriodTreeLeaf> | null;
	period: Partial<IPeriodTreeLeaf> | null;
	expanded: string[];
	selectDefaultLastCommissionCycle?: boolean;
}) => {
	const { selection, period, expanded = [], selectDefaultLastCommissionCycle = false } = args;

	if (selection || !selectDefaultLastCommissionCycle || !period) {
		return expanded;
	}

	return [period?.year ?? "", period?.cycleId ?? ""];
};

function PayPeriodTree(props: Readonly<IPayPeriodTreeProps>) {
	const {
		data = [],
		selected = null,
		selectBy = "_id",
		disabled = false,
		disablePayPeriodNotLast = false,
		isMultiple = false,
		onlyActive = false,
		onlyPayPeriodId = false,
		selectDefaultLastCommissionCycle = false,
		loading = false,
		callbackSelected = () => {},
		customBadges = {},
		defaultByDate = false,
	} = props;

	const locationId = useLocationId();

	const [selection, setSelection] = useState<Partial<IPeriodTreeLeaf> | null>(null);

	const {
		tree,
		defaultPayPeriod,
		expanded: defaultExpanded,
	} = getPayPeriodTree({
		list: data,
		disablePayPeriodNotLast,
		isMultiple,
		onlyActive,
		onlyPayPeriodId,
		customBadges,
		defaultByDate,
	});

	const handleSelection = (element: IPeriodTreeLeaf[] | null) => {
		const period = element?.[0];
		if (period && period?._id !== selection?._id) {
			setSelection(period);
			callbackSelected?.(period, isActiveCommissionCycle(period));
		}
	};

	useEffect(() => {
		let defaultSelection = selected ?? null;
		if (selectDefaultLastCommissionCycle && !selected && defaultPayPeriod) {
			defaultSelection = defaultPayPeriod as IPeriodTreeLeaf;
		}
		callbackSelected?.(defaultSelection, isActiveCommissionCycle(defaultSelection));
		setSelection(defaultSelection);

		return () => {
			setSelection(null);
		};
	}, [selected, props.loading, locationId]);

	// Implementing the selectedItems
	const selectedItems = getOrderedSelectedItems(selection, selectBy, isMultiple);
	const expanded = getExpandedSelection({
		selection,
		selectDefaultLastCommissionCycle,
		period: defaultPayPeriod,
		expanded: defaultExpanded,
	});

	return (
		<AppSideBar
			searchProps={{
				loading,
			}}
			sideBarProps={{
				tree,
				expanded,
				loading,
				disabled,
				selectBy,
				selectedItems,
				defaultSelected: selection ? (selection[selectBy as keyof object] ?? "") : "",
				onSelectAction: handleSelection,
			}}
		/>
	);
}

export default function PayPeriodSideBar(props: Readonly<IPayPeriodTreeProps>) {
	const {
		selected = null,
		selectBy = "_id",
		disabled = false,
		disablePayPeriodNotLast = false,
		isMultiple = false,
		onlyActive = false,
		onlyPayPeriodId = false,
		selectDefaultLastCommissionCycle = false,
		callbackSelected = () => {},
		customBadges = {},
		defaultByDate = false,
	} = props;

	const location = useSelector((state: ILocationState) => state.location.location);

	const { data, isLoading } = useQuery({
		queryKey: ["commissionCycles", location?.locationId],
		queryFn: async () => getCommissionCycles(location?.locationId),
		staleTime: Infinity,
		enabled: !!location?.locationId,
	});

	useEffect(() => {
		if (!location) {
			return;
		}
	}, [location]);

	return (
		<Grid>
			<PayPeriodTree
				data={data ?? []}
				selected={selected}
				selectBy={selectBy}
				disabled={disabled}
				disablePayPeriodNotLast={disablePayPeriodNotLast}
				loading={isLoading}
				isMultiple={isMultiple}
				onlyActive={onlyActive}
				onlyPayPeriodId={onlyPayPeriodId}
				selectDefaultLastCommissionCycle={selectDefaultLastCommissionCycle}
				callbackSelected={callbackSelected}
				customBadges={customBadges}
				defaultByDate={defaultByDate}
			/>
		</Grid>
	);
}
